import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SimpleStoreDetailModal from "modals/store/SimpleStoreDetailModal";
import { requester } from 'App';
import styles from './StoreList.module.css';

function StoreList() {
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('위치 정보를 불러오는 중...');
    const [locationError, setLocationError] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();

    // 위치 정보 획득
    useEffect(() => {
        // iOS 앱에서 위치 정보를 받기 위한 전역 함수 정의
        window.handleLocationFromIOS = (latitude, longitude) => {
            if (!latitude || !longitude) return;
            setUserLocation({
                getLat: () => latitude,
                getLng: () => longitude
            });
            loadStores();
        };

        // 안드로이드 앱에서 위치 정보를 받기 위한 전역 함수 정의
        window.handleLocationFromAndroid = (latitude, longitude) => {
            if (!latitude || !longitude) return;
            setUserLocation({
                getLat: () => latitude,
                getLng: () => longitude
            });
            loadStores();
        };

        // 앱에서 실행되고 있는지 확인
        const checkIsApp = () => {
            try {
                // iOS 앱 체크
                if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.BRIDGE) {
                    window.webkit.messageHandlers.BRIDGE.postMessage('requestLocation');
                    return true;
                }
                // 안드로이드 앱 체크
                if (window.BRIDGE && window.BRIDGE.requestLocation) {
                    window.BRIDGE.requestLocation();
                    return true;
                }
                return false;
            } catch (e) {
                return false;
            }
        };

        // 웹 환경에서 위치 정보 요청
        const requestWebLocation = () => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setUserLocation({
                            getLat: () => position.coords.latitude,
                            getLng: () => position.coords.longitude
                        });
                        loadStores();
                    },
                    (error) => {
                        setLocationError(true);
                        setStatusMessage('위치 정보를 가져올 수 없습니다.');
                    }
                );
            } else {
                setLocationError(true);
                setStatusMessage('위치 정보를 사용할 수 없는 환경입니다.');
            }
        };

        // 앱 여부 확인 후 적절한 위치 정보 요청
        if (!checkIsApp()) {
            requestWebLocation();
        }

        return () => {
            delete window.handleLocationFromIOS;
            delete window.handleLocationFromAndroid;
        };
    }, []);

    // 매장 데이터 로드
    const loadStores = () => {
        requester.findAllByConfirmYnIsTrue((result) => {
            if (result.code === 0 && result.data) {
                const validStores = result.data.filter(store => 
                    store.latitudeByX && 
                    store.longitudeByY && 
                    !isNaN(parseFloat(store.latitudeByX)) && 
                    !isNaN(parseFloat(store.longitudeByY))
                );
                setStores(validStores);
                setIsLoading(false);
            } else {
                setLocationError(true);
                setStatusMessage('매장 정보를 불러오는 데 실패했습니다.');
            }
        });
    };

    // 거리 계산 함수
    const calculateDistance = (store) => {
        if (!userLocation || !store.latitudeByX || !store.longitudeByY) return null;
        
        const lat1 = userLocation.getLat();
        const lon1 = userLocation.getLng();
        const lat2 = parseFloat(store.latitudeByX);
        const lon2 = parseFloat(store.longitudeByY);
        
        const R = 6371e3; // 지구의 반지름 (미터)
        const φ1 = lat1 * Math.PI/180;
        const φ2 = lat2 * Math.PI/180;
        const Δφ = (lat2-lat1) * Math.PI/180;
        const Δλ = (lon2-lon1) * Math.PI/180;

        const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ/2) * Math.sin(Δλ/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

        return Math.round(R * c);
    };

    // 거리순으로 정렬된 매장 목록
    const sortedStores = [...stores].sort((a, b) => {
        const distanceA = calculateDistance(a) || Infinity;
        const distanceB = calculateDistance(b) || Infinity;
        return distanceA - distanceB;
    });

    // 거리 표시 형식 변환 함수
    const formatDistance = (meters) => {
        if (meters >= 1000) {
            return `${(meters / 1000).toFixed(1)}km`;
        }
        return `${meters}m`;
    };

    if (isLoading || !userLocation) {
        return (
            <div className={styles.loading}>
                <p>{statusMessage || '매장 정보를 불러오는 중...'}</p>
            </div>
        );
    }

    if (locationError) {
        return (
            <div className={styles.error}>
                <p>{statusMessage}</p>
                <button onClick={() => window.location.reload()} className={styles.retryButton}>
                    다시 시도
                </button>
            </div>
        );
    }

    if (sortedStores.length === 0) {
        return (
            <div className={styles.empty}>
                <p>주변에 이용 가능한 매장이 없습니다.</p>
            </div>
        );
    }

    return (
        <div className={styles.list}>
            {sortedStores.map(store => {
                const distance = calculateDistance(store);
                return (
                    <div 
                        key={store.id} 
                        className={styles.card}
                        onClick={() => {
                            setSelectedStore(store);
                            setShowModal(true);
                        }}
                    >
                        <div className={styles.info}>
                            <div className={styles.header}>
                                <h3>{store.storeName}</h3>
                                {distance && (
                                    <span className={styles.distance}>
                                        {formatDistance(distance)}
                                    </span>
                                )}
                            </div>
                            <p className={styles.address}>{store.address}</p>
                            {store.storePhoneNumber && (
                                <p className={styles.phone}>{store.storePhoneNumber}</p>
                            )}
                        </div>
                    </div>
                );
            })}

            {showModal && selectedStore && (
                <SimpleStoreDetailModal
                    store={selectedStore}
                    userLocationProp={userLocation ? {
                        latitude: userLocation.getLat(),
                        longitude: userLocation.getLng()
                    } : null}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedStore(null);
                    }}
                    isMapView={true}
                />
            )}
        </div>
    );
}

export default StoreList;